import { ModuleHeader } from "./ModuleHeader";
import styles from "./StubModule.module.css";

export interface ConnectActionProps {
  readonly label: string;
  /**
   * Kept on the interface so existing call sites (useConnectAction) still
   * compile, but no longer invoked from StubModule — the Connect button
   * itself was moved to Settings → Connections. Tracks whether a sign-in
   * attempt is in flight so the badge can show a placeholder.
   */
  readonly onClick?: () => void;
  /**
   * Clears the adapter's tokens and emits a token-change to listeners —
   * wired to the Disconnect button on Settings → Connections. Optional
   * so StubModule callers that don't need it still type-check.
   */
  readonly disconnect?: () => void;
  readonly busy?: boolean;
  readonly error?: string | null;
  readonly connected?: boolean;
}

interface StubModuleProps {
  readonly title: string;
  readonly subtitle: string;
  readonly noticeTitle: string;
  readonly noticeMessage: string;
  readonly features: readonly string[];
  readonly onBack?: () => void;
  readonly connectAction?: ConnectActionProps;
  /**
   * Opens the Settings page (Connections tab). When provided, the
   * disconnected notice gets an "Open Settings" button; otherwise it
   * shows a passive hint so the page still renders in isolation
   * (unit tests, storybook).
   */
  readonly onOpenSettings?: () => void;
}

export function StubModule({
  title,
  subtitle,
  noticeTitle,
  noticeMessage,
  features,
  onBack,
  connectAction,
  onOpenSettings,
}: Readonly<StubModuleProps>) {
  const showDisconnected = connectAction != null && !connectAction.connected;

  return (
    <div className={styles.module}>
      <ModuleHeader title={title} subtitle={subtitle} onBack={onBack} />

      <div className={styles.body}>
        <div className={styles.notice}>
          <p className={styles.noticeTitle}>{noticeTitle}</p>
          <p className={styles.noticeMsg}>{noticeMessage}</p>

          {connectAction?.connected && (
            <output className={styles.connectedBadge}>
              <span className={styles.connectedDot} aria-hidden="true">●</span> Connected
            </output>
          )}

          {showDisconnected && (
            <p className={styles.noticeMsg} style={{ marginTop: 12 }}>
              Connect this service from <strong>Settings → Connections</strong>.
            </p>
          )}

          {showDisconnected && onOpenSettings && (
            <button
              type="button"
              className={styles.connectButton}
              onClick={onOpenSettings}
            >
              Open Settings
            </button>
          )}

          {connectAction?.error && (
            <p className={styles.connectError} role="alert">
              {connectAction.error}
            </p>
          )}
        </div>

        {features.length > 0 && (
          <div className={styles.featureList}>
            <p className={styles.featureTitle}>Features</p>
            {features.map((f) => (
              <div key={f} className={styles.feature}>
                <span className={styles.featureBullet}>•</span>
                <span className={styles.featureText}>{f}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
